'use client'

import { useState } from 'react'
import { Switch } from '@/components/ui/switch'
import { Bell, Lock, Mail, Save, Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

const secondaryNavigation = [
  { name: 'General', href: '#general', current: true, icon: Bell },
  { name: 'Security', href: '#security', current: false, icon: Lock },
  { name: 'Notifications', href: '#notifications', current: false, icon: Mail },
  { name: 'Plan', href: '#plan', current: false, icon: Bell },
  { name: 'Billing', href: '#billing', current: false, icon: Lock },
  { name: 'Team members', href: '#team-members', current: false, icon: Mail },
]

export default function Settings() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [automaticTimezoneEnabled, setAutomaticTimezoneEnabled] = useState(true)
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      browser: false,
      mobile: true,
    },
    privacy: {
      profileVisibility: 'public',
      showEmail: false,
      showPhone: true,
    },
    preferences: {
      language: 'en',
      dateFormat: 'MM/DD/YYYY',
      theme: 'light',
    },
  })
  const [currentSection, setCurrentSection] = useState('general')

  const renderSection = () => {
    switch (currentSection) {
      case 'general':
        return <div>General Settings Content</div>
      case 'security':
        return <div>Security Settings Content</div>
      case 'notifications':
        return (
          <div>
            <h2 className="text-base/7 font-semibold text-gray-900">Notifications</h2>
            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-600" />
                  <span>Email Notifications</span>
                </label>
                <Switch
                  checked={settings.notifications.email}
                  onCheckedChange={e => setSettings(prev => ({
                    ...prev,
                    notifications: { ...prev.notifications, email: e }
                  }))}
                  className={classNames(
                    settings.notifications.email ? 'bg-indigo-600' : 'bg-gray-200',
                                       'relative inline-flex h-6 w-11 items-center rounded-full'
                  )}
                >
                  <span className="sr-only">Enable email notifications</span>
                  <span
                    className={classNames(
                      settings.notifications.email ? 'translate-x-6' : 'translate-x-1',
                                           'inline-block h-4 w-4 transform rounded-full bg-white transition'
                    )}
                  />
                </Switch>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-600" />
                  <span>Browser Notifications</span>
                </label>
                <Switch
                  checked={settings.notifications.browser}
                  onCheckedChange={e => setSettings(prev => ({
                    ...prev,
                    notifications: { ...prev.notifications, browser: e }
                  }))}
                  className={classNames(
                    settings.notifications.browser ? 'bg-indigo-600' : 'bg-gray-200',
                    'relative inline-flex h-6 w-11 items-center rounded-full'
                  )}
                >
                  <span className="sr-only">Enable browser notifications</span>
                  <span
                    className={classNames(
                      settings.notifications.browser ? 'translate-x-6' : 'translate-x-1',
                      'inline-block h-4 w-4 transform rounded-full bg-white transition'
                    )}
                  />
                </Switch>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-600" />
                  <span>Mobile Notifications</span>
                </label>
                <Switch
                  checked={settings.notifications.mobile}
                  onCheckedChange={e => setSettings(prev => ({
                    ...prev,
                    notifications: { ...prev.notifications, mobile: e }
                  }))}
                  className={classNames(
                    settings.notifications.mobile ? 'bg-indigo-600' : 'bg-gray-200',
                    'relative inline-flex h-6 w-11 items-center rounded-full'
                  )}
                >
                  <span className="sr-only">Enable mobile notifications</span>
                  <span
                    className={classNames(
                      settings.notifications.mobile ? 'translate-x-6' : 'translate-x-1',
                      'inline-block h-4 w-4 transform rounded-full bg-white transition'
                    )}
                  />
                </Switch>
              </div>
            </div>
          </div>
        )
      case 'plan':
        return <div>Plan Settings Content</div>
      case 'billing':
        return <div>Billing Settings Content</div>
      case 'team-members':
        return <div>Team Members Settings Content</div>
      default:
        return null
    }
  }

  return (
    <>
      <div className="mx-auto max-w-7xl pt-16 lg:flex lg:gap-x-16 lg:px-8">
        <h1 className="sr-only">General Settings</h1>

        <aside className="flex overflow-x-auto border-b border-gray-900/5 py-4 lg:block lg:w-64 lg:flex-none lg:border-0 lg:py-20">
          <nav className="flex-none px-4 sm:px-6 lg:px-0">
            <ul role="list" className="flex gap-x-3 gap-y-1 whitespace-nowrap lg:flex-col">
              {secondaryNavigation.map((item) => (
                <li key={item.name}>
                  <a
                    href={item.href}
                    onClick={() => {
                      setCurrentSection(item.href.substring(1))
                      secondaryNavigation.forEach(navItem => navItem.current = navItem.name === item.name)
                    }}
                    className={classNames(
                      item.current
                        ? 'bg-gray-50 text-indigo-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                      'group flex gap-x-3 rounded-md py-2 pl-2 pr-3 text-sm/6 font-semibold',
                    )}
                  >
                    <item.icon
                      aria-hidden="true"
                      className={classNames(
                        item.current ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                        'size-6 shrink-0',
                      )}
                    />
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <main className="px-4 py-16 sm:px-6 lg:flex-auto lg:px-0 lg:py-20">
          <div className="mx-auto max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none">
            {renderSection()}
            <div className="flex justify-end">
              <Button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                <Save className="w-4 h-4" />
                Save All Changes
              </Button>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}